import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  TourItineraryReqDTO,
  VendorServiceDetailReqDTO,
  VendorServiceDetailRespDTO,
  VendorServiceDetailSearchReqDTO,
  VendorServiceDetailSearchResDTO,
  serviceApproveOrRejectReqDTO
} from 'src/models/vendor';
import { tourismClassifiedAPIRoutes } from '..';

export const vendorServiceDetail = createApi({
  reducerPath: 'vendorServicesDetail',
  tagTypes: ['VendorServiceTag'],
  baseQuery: fetchBaseQuery({
    baseUrl: tourismClassifiedAPIRoutes.baseUrl,
    prepareHeaders: (headers) => {
      // If we have a token set in local storage, let's assume that we should be passing it.
      const token = localStorage.getItem('jwtToken');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  endpoints: (builder) => ({
    saveVendorServiceDetail: builder.mutation<
      VendorServiceDetailRespDTO,
      VendorServiceDetailReqDTO
    >({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.vendorServiceDetail.endpoint}/${tourismClassifiedAPIRoutes.vendorServiceDetail.saveVendorServiceDetail}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['VendorServiceTag']
    }),

    updateVendorServiceDetail: builder.mutation<
      VendorServiceDetailRespDTO,
      VendorServiceDetailReqDTO
    >({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.vendorServiceDetail.endpoint}/${tourismClassifiedAPIRoutes.vendorServiceDetail.updateVendorServiceDetail}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: ['VendorServiceTag']
    }),

    getServiceDetailBySearchCriteria: builder.query<
      VendorServiceDetailSearchResDTO,
      VendorServiceDetailSearchReqDTO
    >({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.vendorServiceDetail.endpoint}/${tourismClassifiedAPIRoutes.vendorServiceDetail.getServiceDetailBySearchCriteria}`,
        method: 'POST',
        body
      }),
      providesTags: ['VendorServiceTag']
    }),

    findVendorServiceDetailById: builder.mutation({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.vendorServiceDetail.endpoint}/${tourismClassifiedAPIRoutes.vendorServiceDetail.findVendorServiceDetailById}/${body.id}/${body.customerId}`,
        method: 'GET'
      })
    }),

    deleteVendorServiceDetail: builder.mutation<boolean, number>({
      query: (id) => ({
        url: `${tourismClassifiedAPIRoutes.vendorServiceDetail.endpoint}/${tourismClassifiedAPIRoutes.vendorServiceDetail.deleteVendorServiceDetail}?vendorServiceDetailId=${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['VendorServiceTag']
    }),

    getAllToursItineraryByServiceId: builder.query<
      TourItineraryReqDTO[],
      number
    >({
      query: (id) => ({
        url: `${tourismClassifiedAPIRoutes.vendorServiceDetail.endpoint}/${tourismClassifiedAPIRoutes.vendorServiceDetail.getAllToursItineraryByServiceId}?serviceDetailId=${id}`,
        method: 'GET'
      }),
      providesTags: ['VendorServiceTag']
    }),

    saveToursItinerary: builder.mutation({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.vendorServiceDetail.endpoint}/${tourismClassifiedAPIRoutes.vendorServiceDetail.saveToursItinerary}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['VendorServiceTag']
    }),

    updateToursItinerary: builder.mutation<
      TourItineraryReqDTO,
      TourItineraryReqDTO
    >({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.vendorServiceDetail.endpoint}/${tourismClassifiedAPIRoutes.vendorServiceDetail.updateToursItinerary}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: ['VendorServiceTag']
    }),

    deleteToursItineraryById: builder.mutation<boolean, number>({
      query: (id) => ({
        url: `${tourismClassifiedAPIRoutes.vendorServiceDetail.endpoint}/${tourismClassifiedAPIRoutes.vendorServiceDetail.deleteToursItineraryById}?toursItineraryId=${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['VendorServiceTag']
    }),

    savePackageHotelDetail: builder.mutation({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.vendorServiceDetail.endpoint}/${tourismClassifiedAPIRoutes.vendorServiceDetail.savePackageHotelDetail}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['VendorServiceTag']
    }),

    getAllHotelDetailSearchCriteria: builder.mutation({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.vendorServiceDetail.endpoint}/${tourismClassifiedAPIRoutes.vendorServiceDetail.getPackageHotelDetail}`,
        method: 'POST',
        body: body
      }),
      invalidatesTags: ['VendorServiceTag']
    }),

    deleteHotelDetailById: builder.mutation<boolean, number>({
      query: (id) => ({
        url: `${tourismClassifiedAPIRoutes.vendorServiceDetail.endpoint}/${tourismClassifiedAPIRoutes.vendorServiceDetail.deleteHotelDetailById}?hotelDetailId=${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['VendorServiceTag']
    }),

    updateHotelDetail: builder.mutation<any, any>({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.vendorServiceDetail.endpoint}/${tourismClassifiedAPIRoutes.vendorServiceDetail.updateHotelDetail}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: ['VendorServiceTag']
    }),

    getAllInclusionExclusionDetailByServiceIdAndType: builder.mutation({
      query: ({ id, value }) => ({
        url: `${tourismClassifiedAPIRoutes.vendorServiceDetail.endpoint}/${tourismClassifiedAPIRoutes.vendorServiceDetail.getAllInclusionExclusionDetailByServiceIdAndType}?serviceDetailId=${id}&type=${value}`,
        method: 'GET'
      }),
      invalidatesTags: ['VendorServiceTag']
    }),

    saveAndUpdateInclusionExclutionDetail: builder.mutation({
      query: ({ body, type }) => ({
        url: `${tourismClassifiedAPIRoutes.vendorServiceDetail.endpoint}/${tourismClassifiedAPIRoutes.vendorServiceDetail.saveAndUpdateInclusionExclutionDetail}?type=${type}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['VendorServiceTag']
    }),

    approveOrRejectServiceDetail: builder.mutation<
      serviceApproveOrRejectReqDTO,
      any
    >({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.vendorServiceDetail.endpoint}/${tourismClassifiedAPIRoutes.vendorServiceDetail.approveOrRejectServiceDetail}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['VendorServiceTag']
    })
  })
});
export const {
  useSaveVendorServiceDetailMutation,
  useGetServiceDetailBySearchCriteriaQuery,
  useDeleteToursItineraryByIdMutation,
  useGetAllToursItineraryByServiceIdQuery,
  useSaveToursItineraryMutation,
  useUpdateToursItineraryMutation,
  useFindVendorServiceDetailByIdMutation,
  useUpdateVendorServiceDetailMutation,
  useDeleteVendorServiceDetailMutation,
  useSavePackageHotelDetailMutation,
  useGetAllHotelDetailSearchCriteriaMutation,
  useDeleteHotelDetailByIdMutation,
  useUpdateHotelDetailMutation,
  useApproveOrRejectServiceDetailMutation,
  useGetAllInclusionExclusionDetailByServiceIdAndTypeMutation,
  useSaveAndUpdateInclusionExclutionDetailMutation
} = vendorServiceDetail;
